import * as z from "zod";
import { addressTypeSchema } from "@/components/molecules/SelectInputs/SelectAddressType/addressTypeSchema";
import { BankAccountValidationSchema } from "@/components/pages/Settings/validation/bankAccountValidationSchema";
import AddressTypeEnum from "@/constants/enums/AddressTypeEnum";
import CompanyTypeEnum from "@/constants/enums/CompanyTypeEnum";

const optionalTrimmedString = (max: number, message: string) =>
  z.preprocess(
    (val) => (val === "" || val === null || val === undefined ? undefined : val),
    z.string().max(max, { message }).optional()
  );

// The backend only lets companies self-register as these types (USR-253 otherwise).
export const REGISTRATION_COMPANY_TYPES = [CompanyTypeEnum.TRADER, CompanyTypeEnum.TRANSPORT] as const;

const registrationCompanyTypeSchema = z.enum(REGISTRATION_COMPANY_TYPES, {
  errorMap: () => ({ message: "validation:companyTypeRequired" }),
});

export const AddressItemSchema = z.object({
  address: z.preprocess(
    (v) => v ?? "",
    z.string().min(1, { message: "validation:addressRequired" }).max(100, { message: "validation:addressMaxLength" })
  ),
  address2: optionalTrimmedString(100, "validation:addressMaxLength"),
  address3: optionalTrimmedString(100, "validation:addressMaxLength"),
  // Required here because AccountGate treats a company without a post code as incomplete.
  postCode: z.preprocess(
    (v) => v ?? "",
    z.string().min(1, { message: "validation:postCodeRequired" }).max(10, { message: "validation:postCodeMaxLength" })
  ),
  place: z.preprocess((v) => v ?? "", z.string().min(1, { message: "validation:placeRequired" })),
  // Saved drafts come back from the backend with regionId: null.
  regionId: z.preprocess((v) => (v == null ? undefined : v), z.number().optional()),
  countryId: z.preprocess(
    (v) => (v == null ? 0 : Number(v)),
    z.number().min(1, { message: "validation:countryRequired" })
  ),
  addressType: addressTypeSchema,
});

const leiNumberSchema = z.preprocess(
  (val) => (val === "" || val === null || val === undefined ? undefined : val),
  z
    .string()
    .superRefine((val, ctx) => {
      if (!/^[a-zA-Z0-9]*$/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation:leiNumberAlphanumeric",
        });
      } else if (val.length < 20) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          type: "string",
          minimum: 20,
          inclusive: true,
          message: "validation:leiNumberLength",
        });
      }
    })
    .optional()
    .nullable()
);

const eoriNumberSchema = z.preprocess(
  (val) => (val === "" || val === null || val === undefined ? undefined : val),
  z
    .string()
    .regex(/^[A-Z]{2}[A-Z0-9]+$/i, { message: "validation:eoriNumberFormat" })
    .optional()
    .nullable()
);

const PartialCompanySchema = z.object({
  name: z.string().min(1, { message: "validation:companyNameRequired" }),
  email: z.string().min(1, { message: "validation:emailRequired" }).email({ message: "validation:emailFormatError" }),
  vatNumber: z
    .string()
    .min(1, { message: "validation:vatNumberRequired" })
    .max(15, { message: "validation:vatNumberMaxLength" }),
  leiNumber: leiNumberSchema,
  eoriNumber: eoriNumberSchema,
  phone: z.preprocess(
    (v) => (v === "" || v == null ? undefined : v),
    z
      .string({ required_error: "validation:phoneNumberRequired" })
      .max(30, { message: "validation:phoneNumberMaxLength" })
  ),
  address: z
    .array(AddressItemSchema)
    .min(1, { message: "validation:atLeastOneAddressRequired" })
    .refine((arr) => arr.some((a) => a.addressType === AddressTypeEnum.BILLING), {
      message: "validation:billingAddressRequired",
    }),
  countryCodeIso3: z.string().min(1, { message: "validation:countryRequired" }),
  languageId: z.number().min(1, { message: "validation:languageRequired" }),
  bankAccount: z.array(BankAccountValidationSchema),
  defaultCurrency: z.string().min(1, { message: "validation:ibanRequired" }),
  companyProvidesTransport: z.boolean().default(false),
  companyType: registrationCompanyTypeSchema,
  timeZone: z.string().min(1, { message: "validation:timeZoneRequired" }),
  admin: z.object({
    firstname: z.string().trim().min(1, { message: "validation:firstNameRequired" }),
    lastname: z.string().trim().min(1, { message: "validation:lastNameRequired" }),
    email: z.string().min(1, { message: "validation:emailRequired" }).email({ message: "validation:emailFormatError" }),
    roleType: z.string().min(1, { message: "validation:roleTypeRequired" }),
  }),
  teamMembers: z
    .array(
      z.object({
        firstname: z.string().min(1, { message: "validation:firstNameRequired" }),
        lastname: z.string().min(1, { message: "validation:lastNameRequired" }),
        email: z
          .string()
          .min(1, { message: "validation:emailRequired" })
          .email({ message: "validation:emailFormatError" }),
        roleType: z.string().min(1, { message: "validation:roleTypeRequired" }),
      })
    )
    .optional(),
  membershipType: z.string().min(1, { message: "validation:membershipTypeRequired" }),
  terms: z.boolean().refine((v) => v, { message: "validation:termsRequired" }),
  creditLimit: z.number().min(0, { message: "validation:creditLimitRequired" }),
  invoiceDaysBeforeDueDate: z.preprocess(
    (v) => (v == null || v === 0 ? undefined : Number(v)),
    z
      .number({ required_error: "validation:invoiceBeforeDueDateRequired" })
      .min(1, { message: "validation:invoiceBeforeDueDateRequired" })
  ),
  settlementDays: z.preprocess(
    (v) => (v == null || v === 0 ? undefined : Number(v)),
    z
      .number({ required_error: "validation:settlementDaysRequired" })
      .min(1, { message: "validation:settlementDaysRequired" })
  ),
});

const validationSchema = z.object({
  partialCompanyId: z.number().min(1).nullable(),
  userId: z.number().min(1).nullable(),
  partialCompany: PartialCompanySchema,
  updated: z.string().optional(),
  submitted: z.boolean().optional(),
  errors: z
    .array(
      z.object({
        type: z.string(),
        description: z.string(),
        field: z.string(),
      })
    )
    .optional(),
});

export const RegisterCompanyValidationSchema = validationSchema;
export type RegisterCompanyFormInput = z.infer<typeof RegisterCompanyValidationSchema>;
